import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/layout/MetaTags';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import FadeInView from '../../components/FadeInView';
import { useBlogPosts } from '../../hooks/useBlog';
import { CATEGORIES, getCategoryStyles } from '../../constants/categories';
import { IoNewspaper, IoMegaphone, IoTrophy, IoDocumentText, IoList, IoSearch, IoStar, IoTime, IoArrowForward } from 'react-icons/io5';
import { BlogSkeleton } from '../../components/SkeletonLoader';
import './Blog.css';

// Calculate reading time
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  const text = content.replace(/<[^>]*>/g, '');
  const wordCount = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const categoryIcons = {
  NEWS: IoNewspaper,
  ANNOUNCEMENT: IoMegaphone,
  ACHIEVEMENT: IoTrophy,
  BLOG: IoDocumentText,
};

const Blog = () => {
  const { posts, loading, error, page, totalPages, setPage } = useBlogPosts();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Filter posts by category and search query
  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === null || post.category.toUpperCase() === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Get featured post (first post) and remaining posts
  const featuredPost = filteredPosts.length > 0 ? filteredPosts[0] : null;
  const remainingPosts = filteredPosts.slice(1);

  const resetFilters = () => {
    setSelectedCategory(null);
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="blog-page">
        <MetaTags
          title="Blog & Berita - YBUS"
          description="Temukan artikel, berita, dan pengumuman terbaru seputar kegiatan di Pondok Pesantren Bahrul Ulum."
        />
        <Header />
        <main className="blog-main">
          <div className="container-tight">
            <BlogSkeleton />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-page">
        <MetaTags
          title="Blog & Berita - YBUS"
          description="Temukan artikel, berita, dan pengumuman terbaru seputar kegiatan di Pondok Pesantren Bahrul Ulum."
        />
        <Header />
        <main className="blog-main">
          <div className="container-tight">
            <div className="blog-error">
              <p>Error loading blog posts. Please try again later.</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
 
  return (
    <div className="blog-page">
      <MetaTags
        title="Blog & Berita - YBUS"
        description="Temukan artikel, berita, dan pengumuman terbaru seputar kegiatan di Pondok Pesantren Bahrul Ulum."
      />
      <Header />
      
      <main className="blog-main">
        <div className="container-tight">
          {/* Header */}
          <FadeInView className="blog-header">
            <span className="text-label blog-label">BLOG & BERITA</span>
            <h1 className="text-headline blog-title">
              Update Terkini
              <br />
              <span className="blog-title-muted">dari Kami</span>
            </h1>
          </FadeInView>

          {/* Search Bar */}
          <FadeInView delay={0.1} className="blog-search-wrapper">
            <div className="blog-search-container">
              <IoSearch className="blog-search-icon" />
              <input
                type="text"
                className="blog-search-input"
                placeholder="Cari artikel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </FadeInView>

          {/* Category Filters */}
          <FadeInView delay={0.2} className="blog-filters">
            <button
              className={`blog-filter ${selectedCategory === null ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              <IoList /> Semua
            </button>
            {Object.entries(CATEGORIES).map(([key, { label }]) => {
              const Icon = categoryIcons[key] || IoDocumentText;
              return (
                <button
                  key={key}
                  className={`blog-filter ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(key)}
                >
                  <Icon /> {label}
                </button>
              );
            })}
          </FadeInView>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <FadeInView className="blog-empty">
              <IoNewspaper className="blog-empty-icon" />
              <h3>Tidak ada artikel ditemukan</h3>
              <p>Coba ubah filter atau kata kunci pencarian Anda</p>
              <button className="blog-reset-btn" onClick={resetFilters}>
                Reset Filter
              </button>
            </FadeInView>
          )}

          {/* Featured Post */}
          {featuredPost && (
            <FadeInView delay={0.1}>
              <Link to={`/blog/${featuredPost.slug}`} className="blog-featured">
                <div className="blog-featured-image">
                  <span className="blog-featured-badge">
                    <IoStar /> Terbaru
                  </span>
                  <img 
                    src={featuredPost.image_url || 'https://via.placeholder.com/800x600?text=Bahrul+Ulum'} 
                    alt={featuredPost.title}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Bahrul+Ulum';
                    }}
                  />
                </div>
                <div className="blog-featured-content">
                  <div className="blog-featured-meta">
                    <span 
                      className="blog-featured-category"
                      style={getCategoryStyles(featuredPost.category)}
                    >
                      {CATEGORIES[featuredPost.category.toUpperCase()]?.label}
                    </span>
                    <span className="blog-featured-date">
                      {new Date(featuredPost.created_at).toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="blog-featured-reading">
                      <IoTime /> {calculateReadingTime(featuredPost.content)} menit baca
                    </span>
                  </div>
                  <h2 className="blog-featured-title">{featuredPost.title}</h2>
                  <p className="blog-featured-excerpt">{featuredPost.excerpt}</p>
                  <span className="blog-featured-link">
                    Baca Selengkapnya <IoArrowForward />
                  </span>
                </div>
              </Link>
            </FadeInView>
          )}

          {/* Blog Grid */}
          {remainingPosts.length > 0 && (
            <div className="blog-grid">
              {remainingPosts.map((post, index) => (
                <FadeInView key={post.id} delay={index * 0.08}>
                  <Link to={`/blog/${post.slug}`} className="blog-card">
                    <div className="blog-card-image-wrapper">
                      <span 
                        className="blog-card-category"
                        style={getCategoryStyles(post.category)}
                      >
                        {CATEGORIES[post.category.toUpperCase()]?.label}
                      </span>
                      <img 
                        src={post.image_url || 'https://via.placeholder.com/800x600?text=Bahrul+Ulum'} 
                        alt={post.title}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Bahrul+Ulum';
                        }}
                      />
                    </div>
                    <div className="blog-card-content">
                      <div className="blog-card-meta">
                        <span className="blog-card-date">
                          {new Date(post.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="blog-card-reading">
                          <IoTime /> {calculateReadingTime(post.content)} min
                        </span>
                      </div>
                      <h3 className="blog-card-title">{post.title}</h3>
                      <p className="blog-card-excerpt">{post.excerpt}</p>
                      <span className="blog-card-link">
                        Baca Selengkapnya <IoArrowForward />
                      </span>
                    </div>
                  </Link>
                </FadeInView>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <FadeInView className="blog-pagination">
              <button
                className="blog-pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                ← Sebelumnya
              </button>
              <span className="blog-pagination-info">
                Halaman {page} dari {totalPages}
              </span>
              <button
                className="blog-pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Selanjutnya →
              </button>
            </FadeInView>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;
